// Логика заданий (FR-QUIZ-01…06, 11): оценка ответа, анимации-последствия, события прогресса.
import { buildStep, type Point, type BoardHandle } from "@brovka/board";
import type { CompiledScenario, Grade, Quiz, QuizFrame, Shape, StepTimeline } from "@brovka/content-types";
import { CELL, ZoneLookup, loadEngine } from "@brovka/tactics-wasm";
import { endPose, poseAt } from "@brovka/board";
import type { Stage } from "../lib/db.ts";
import { record } from "../lib/events.ts";
import { gridStage } from "../lib/content/catalog.ts";

export type Variant = "original" | "mirror";

export function frameOf(s: CompiledScenario, quizId: string, variant: Variant): QuizFrame | undefined {
  const q = s.quizzes[quizId];
  return q?.variants[variant] ?? q?.variants.original;
}

const lookups = new Map<string, ZoneLookup>();

/** Поиск по сетке зон (≤ 1 мс): перетаскивание — по центру героя, касание — с допуском 0,8 м. */
export async function zoneGrade(s: CompiledScenario, frame: QuizFrame, stage: Stage, p: Point, touch: boolean): Promise<number | undefined> {
  const st = gridStage(frame.grids, stage);
  const grid = st ? frame.grids[st] : undefined;
  if (!grid) return undefined;
  await loadEngine();
  const key = `${s.scenario.id}/${grid.rle.length}/${grid.rle.slice(0, 64)}/${st}`;
  let z = lookups.get(key);
  if (!z) {
    z = new ZoneLookup(JSON.stringify(grid), s.field.lengthM, s.field.widthM);
    lookups.set(key, z);
  }
  return z.grade(p[0], p[1], touch);
}

export function cellsOf(frame: QuizFrame, stage: Stage): { cols: number; rows: number; cellM: number; cells: Uint8Array } | null {
  const st = gridStage(frame.grids, stage);
  const grid = st ? frame.grids[st] : undefined;
  if (!grid) return null;
  const bin = atob(grid.rle);
  const out = new Uint8Array(grid.cols * grid.rows);
  let i = 0;
  for (let k = 0; k < bin.length; k++) {
    const b = bin.charCodeAt(k);
    out.fill(b >> 6, i, i + (b & 0x3f) + 1);
    i += (b & 0x3f) + 1;
  }
  return { cols: grid.cols, rows: grid.rows, cellM: grid.cellM, cells: out };
}

function inShape(shape: Shape, p: Point, field: { lengthM: number; widthM: number }): boolean {
  if (shape.kind === "rect") return p[0] >= shape.at[0] && p[0] <= shape.at[0] + shape.w && p[1] >= shape.at[1] && p[1] <= shape.at[1] + shape.h;
  if (shape.kind === "circle") {
    const dx = ((p[0] - shape.center[0]) / 100) * field.lengthM;
    const dy = ((p[1] - shape.center[1]) / 100) * field.widthM;
    return Math.hypot(dx, dy) <= shape.radius_m;
  }
  let inside = false;
  const pts = shape.points;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]!;
    const [xj, yj] = pts[j]!;
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

const mirrorShape = (s: Shape): Shape =>
  s.kind === "rect" ? { ...s, at: [s.at[0], 100 - s.at[1] - s.h] } : s.kind === "circle" ? { ...s, center: [s.center[0], 100 - s.center[1]] } : { ...s, points: s.points.map((p) => [p[0], 100 - p[1]]) };

/** Оценка «куда встать» с учётом исключений автора. */
export async function gradeWhere(s: CompiledScenario, quiz: Extract<Quiz, { type: "whereToRun" }>, variant: Variant, stage: Stage, p: Point, touch: boolean): Promise<Grade> {
  const frame = frameOf(s, quiz.id, variant);
  if (!frame) return "bad";
  const cell = await zoneGrade(s, frame, stage, p, touch);
  if (cell === CELL.exclusion) {
    const ex = quiz.zones.exclusions.find((e) => inShape(variant === "mirror" ? mirrorShape(e.shape) : e.shape, p, s.field));
    return ex?.grade ?? "bad";
  }
  return cell === CELL.great ? "great" : cell === CELL.ok ? "ok" : "bad";
}

/** Оценка адресата паса (вычислена движком по free, laneOpen, reachable). */
export function gradePass(frame: QuizFrame, stage: Stage, target: string): Grade {
  const st = gridStage(frame.passTargets, stage);
  return (st ? frame.passTargets[st] : undefined)?.find((t) => t.target === target)?.grade ?? "bad";
}

/** Отразить шаг по ширине поля (ветка-последствие в зеркальном варианте). */
export function mirrorStep(step: StepTimeline): StepTimeline {
  const m = (p: [number, number]): [number, number] => [p[0], 100 - p[1]];
  const mp = (pos: StepTimeline["start"]) => Object.fromEntries(Object.entries(pos).map(([k, v]) => [k, v ? m(v) : v]));
  return { ...step, start: mp(step.start), end: mp(step.end), segments: step.segments.map((g) => ({ ...g, path: g.path.map(m) })), overlays: [] };
}

/** Последствие ответа «куда встать»: герой бежит; хорошо — пас ему, плохо — соперник забирает мяч. */
export function whereConsequence(s: CompiledScenario, frame: QuizFrame, hero: string, to: Point, grade: Grade): StepTimeline {
  const start = Object.fromEntries(Object.entries(frame.positions).filter(([, v]) => v)) as Record<string, Point>;
  const owner = frame.owner ?? null;
  const moves: Parameters<typeof buildStep>[2] = [{ kind: "run", obj: hero, to }];
  if (owner && owner !== hero) {
    if (grade === "bad" && frame.presser) {
      const o = start[owner]!;
      const mid: Point = [(o[0] + to[0]) / 2, (o[1] + to[1]) / 2];
      moves.push({ kind: "run", obj: frame.presser, to: mid }, { kind: "pass", to: frame.presser });
    } else {
      moves.push({ kind: "pass", to: hero });
    }
  }
  return buildStep(start, owner, moves, s.field);
}

/** Последствие паса: дошёл до партнёра или перехвачен ближайшим соперником. */
export function passConsequence(s: CompiledScenario, frame: QuizFrame, target: string, grade: Grade): StepTimeline {
  const start = Object.fromEntries(Object.entries(frame.positions).filter(([, v]) => v)) as Record<string, Point>;
  const owner = frame.owner ?? null;
  if (grade === "bad" && frame.presser && owner) {
    const a = start[owner]!;
    const b = start[target] ?? a;
    const mid: Point = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    return buildStep(start, owner, [{ kind: "run", obj: frame.presser, to: mid }, { kind: "pass", to: frame.presser }], s.field);
  }
  return buildStep(start, owner, [{ kind: "pass", to: target }], s.field);
}

/** Проиграть шаг на доске; промис — по окончании. */
export function runStep(board: BoardHandle | null, step: StepTimeline, speed = 1): Promise<void> {
  return new Promise((resolve) => {
    if (!board) return resolve();
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      board.setPose(endPose(step));
      setTimeout(resolve, 300);
      return;
    }
    const t0 = performance.now();
    const tick = (now: number) => {
      const t = ((now - t0) / 1000) * speed;
      board.setPose(poseAt(step, Math.min(t, step.duration)));
      if (t >= step.duration) resolve();
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/** Записать ответ в журнал прогресса (FR-QUIZ-10). */
export async function recordAnswer(childId: string | undefined, s: CompiledScenario, quizId: string, variant: Variant, attempt: number, grade: Grade, responseMs: number) {
  if (!childId) return;
  await record(childId, "quiz_answered", {
    quiz_id: `${s.scenario.id}/${quizId}`,
    content_version: s.version,
    variant,
    attempt,
    grade,
    principles: [s.scenario.principles.main],
    response_ms: Math.round(responseMs),
  });
}
